/* eslint-disable camelcase */
import React, { useState, useEffect, useRef, forwardRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import _isEmpty from "lodash/isEmpty"
import cx from "classnames"
import PropTypes from "prop-types"
import { useRouter } from "next/router"

import { getShortenedUrl } from "@/utils/helper"
import {
  addDataLayerFavGuestUser,
  getAnalyticsData,
  replaceAddToCartDatalayerPush,
} from "@/components/ProductDetail/v3/AddToCart/analytics"
import {
  callFavorite,
  handleProductSuggestion,
} from "@/components/ProductDetail/v3/AddToCart/utils"

import {
  selectAuthState,
  setAuthModalVisibility,
} from "@/store/features/authSlice"
import { selectGenericState } from "@/store/features/genericSlice"
import { compareState } from "@/store/features/compareSlice"

import Button from "@/components/core/Button/Button"
import Checkbox from "@/components/core/Checkbox/Checkbox"
import Tooltip from "@/components/core/ToolTip/ToolTip"

import AddToCartActions from "@/components/ProductDetail/v3/AddToCart/AddToCartActions"
import OutOfStockSection from "@/components/ProductDetail/v3/AddToCart/OutOfStockSection"

import toolTip from "@/public/icons/tooltip.svg"
import wishIcon from "@/public/icons/wish.svg"
import filledWishIcon from "@/public/icons/wishlist-gray-filled.svg"
import infoIcon from "@/public/icons/warning.svg"
import styles from "@/components/ProductDetail/v3/AddToCart/index.module.scss"
import SaveToList from "@/components/SaveToList"
import ListConfirmationModal from "@/components/ListConfirmationModal"
import { getFavIconStatus } from "@/components/ProductList/v2/ProductTile/utils"
import {
  getMyAccountListsWithLineItems,
  selectFavoritesState,
} from "@/store/features/favoritesSlice"
import {
  dataLayerHeartClickSaveListButton,
  dataLayerSavetolistGuest,
} from "@/components/SaveToList/analytics"
import {
  WISHLIST_DELETE,
  WISHLIST_SAVE,
} from "@/components/SaveToList/constant"
import { createContextLogger } from "@/utils/logger/client"

const AddToCart = forwardRef((props, ref) => {
  const {
    isDiscontinued = false,
    isColorDiscontinued = false,
    compareData = {},
    sku = "",
    ProductPriceSpiderIncluded_s = "",
    price = 0,
    backOrder = "No",
    specialOrder = "No",
    staticTexts = {},
    onRemove = () => {},
    onAdd = () => {},
    ctProductId = "",
    productData = {},
    replaceAddToCartCtaLabel = "",
    replaceAddToCartCtaLink = "",
    onAvailableColor = () => {},
    disableEcommerce = false,
    isRegionSoldOnline = true,
    findStoreUrl = "",
    replaceAddToCart = false,
    linkNewTab = false,
    disableRatings = false,
    handleNavChange = () => {},
    productCategory = "",
    favItem = [],
    isPdpPropsLoaded = false,
    analyticData = {},
    isBundleProduct = false,
    onQtyChange = () => {},
    ctData = {},
    resetSelectedFav = () => {},
    showFavirote = false,
    leadTime,
    isBackorderable,
    productOnStock = 1,
    productInfo = {},
    masterSku,
  } = props || {}

  const logger = createContextLogger("pdp:add-to-cart")
  const router = useRouter()
  const { isAuth } = useSelector(selectAuthState)
  const { toaster, featureFlags = {} } = useSelector(selectGenericState)
  const { userListItemsSkus = {} } = useSelector(selectFavoritesState)
  const { isEnable } = useSelector(compareState)
  const [isChecked, setIsChecked] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const dispatch = useDispatch()

  const enableFav = isDiscontinued || isColorDiscontinued ? true : false

  const [showBuyBoxArea, setShowBuyBoxArea] = useState(true)
  const [stockAlert, setStockAlert] = useState({ show: false, mssg: "" })
  const [productStock, setProductStock] = useState(productOnStock)
  const [loginFavouriteCallback, setLoginFavouriteCallback] = useState(null)
  const addToCartButton = useRef(null)
  const localStoreOrShowroomRef = useRef(null)
  const inlineRatingRef = useRef(null)
  const compareCheckboxRef = useRef(null)
  const compareWasFocusedRef = useRef(false)
  const [outOfStockMsg, setOutOfStockMsg] = useState("")
  const [showAddToList, setShowAddToList] = useState(false)
  const [productSavedToList, setProductSavedToList] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [deleteFromList, setDeleteFromList] = useState(false)

  const { ENABLE_SHOPPING_LISTS_V2: isSaveProductToList = false } = featureFlags

  const COMPARE_VERSION_CHECK = process.env.NEXT_PUBLIC_COMPARE_PRODUCTS_VERSION

  const [toasterContent, setToasterContent] = useState({
    successMessage: "",
    unfavoriteSuccessMessage: "",
  })

  const {
    availColorCTA = "",
    compareTxt = "",
    favTxt = "",
    outOfStockMsg: outOfStockMsgTxt,
    addToCart: addToCartTxt,
    ariaLabel: { addProductToCart } = "",
    saveToListText = "",
    savedToListText = "",
    deleteFromListText = "",
  } = staticTexts

  const [listActionText, setListActionText] = useState(saveToListText)

  const handleFavorite = (e, getFavIconStatus) => {
    const isFavIcon =
      isSaveProductToList && getFavIconStatus === filledWishIcon?.src
    const headerTitle = isFavIcon ? "delete from list" : "save to list"
    const productInfoData = JSON.parse(
      productAnalyticsData.stringProductInfo ?? "{}"
    )
    const productInfo = productInfoData?.productInfo
    e.preventDefault()
    e.stopPropagation()
    dataLayerHeartClickSaveListButton(
      productInfo,
      headerTitle,
      isFavIcon ? WISHLIST_DELETE : WISHLIST_SAVE
    )
    if (isSaveProductToList) {
      if (userListItemsSkus[sku]) {
        setOpenModal(true)
        setDeleteFromList(true)
        return
      }

      const element = document.querySelector(
        ".product-detail-page__right-sticky"
      )
      element?.classList.add("pdp-my-list")
      setShowAddToList(true)
      window.loginFavouriteCallback = null
      setLoginFavouriteCallback(null)
      return
    }

    const favoriteData = {
      isFavorite: showFavirote,
      favItem,
      staticTexts,
      ctProductId,
      isAuth,
      toasterContent,
      sku,
      ctData,
    }
    callFavorite(e, favoriteData, handleFavoriteSku, logger)
  }

  const handleFavoriteSku = fItems => {
    resetSelectedFav(fItems, sku)
  }

  const handleSaveToListSuccessCallBack = () => {
    setListActionText(savedToListText)
    setListActionText(deleteFromListText)
  }

  const handleDeleteFromListSuccessCallback = () => {
    setListActionText(saveToListText)
  }

  const handleListSaveAndCreateClose = () => {
    setShowAddToList(false)
    const element = document.querySelector(".product-detail-page__right-sticky")
    element?.classList.remove("pdp-my-list")
  }

  useEffect(() => {
    if (userListItemsSkus[sku]) {
      setListActionText(deleteFromListText)
    } else {
      setListActionText(saveToListText)
    }
  }, [userListItemsSkus, sku])

  useEffect(() => {
    if (isSaveProductToList) {
      if (productSavedToList || userListItemsSkus[sku]) {
        setListActionText(deleteFromListText)
      } else {
        setListActionText(saveToListText)
      }
    } else {
      setListActionText(favTxt)
    }
  }, [ctData?.id, userListItemsSkus])

  useEffect(() => {
    if (outOfStockMsgTxt) {
      getShortenedUrl(outOfStockMsgTxt).then(res =>
        setOutOfStockMsg(res ?? outOfStockMsgTxt)
      )
    }
  }, [outOfStockMsgTxt])

  useEffect(() => {
    if (loginFavouriteCallback && isAuth) {
      if (isSaveProductToList) {
        dispatch(getMyAccountListsWithLineItems())
          .unwrap()
          .then(res => {
            const userlistSKUS = res[0][1]
            if (userlistSKUS[sku]) {
              setOpenModal(true)
              setDeleteFromList(true)
            } else {
              // Only open the list UI, do NOT trigger analytics or call handleFavorite again
              const element = document.querySelector(
                ".product-detail-page__right-sticky"
              )
              element?.classList.add("pdp-my-list")
              setShowAddToList(true)
              window.loginFavouriteCallback = null
              setLoginFavouriteCallback(null)
            }
          })
          .catch(error => {
            logger.error("Error fetching user lists", { error, sku })
          })
      } else {
        // For non-list favorite, keep previous behavior
        const productInfoData = JSON.parse(
          productAnalyticsData.stringProductInfo ?? "{}"
        )
        const productInfo = productInfoData?.productInfo
        dataLayerHeartClickSaveListButton(
          productInfo,
          "save to list",
          WISHLIST_SAVE
        )
        handleFavorite(loginFavouriteCallback)
      }
    }
  }, [loginFavouriteCallback, isAuth])

  useEffect(() => {
    setProductStock(productOnStock)
  }, [productOnStock])

  useEffect(() => {
    if (compareData?.comparedProducts?.find(prod => prod?.compareSku === sku))
      setIsChecked(true)
    else setIsChecked(false)
  }, [compareData?.comparedProducts, sku])

  useEffect(() => {
    setShowBuyBoxArea(!ProductPriceSpiderIncluded_s)
  }, [ProductPriceSpiderIncluded_s, price])

  const setToasterMessage = async () => {
    if (!_isEmpty(toaster?.content)) {
      const { successMessage, unfavoriteSuccessMessage } = toaster.content
      setToasterContent({
        successMessage: await getShortenedUrl(successMessage),
        unfavoriteSuccessMessage: await getShortenedUrl(
          unfavoriteSuccessMessage
        ),
      })
    }
  }

  const handleCompare = e => {
    if (compareCheckboxRef.current) {
      compareCheckboxRef.current.focus()
      compareWasFocusedRef.current =
        compareCheckboxRef.current === document.activeElement
    }
    const nextChecked = e.target.checked
    setIsChecked(nextChecked)
    if (nextChecked) {
      if (compareData?.comparedProducts?.length < 4) {
        let variantFlag = false
        const allVariantSkus = compareData.comparedProducts
          ?.map(a => {
            const variantSkus = a["variantList.sku_ss"] || []
            return [...variantSkus, a.masterSKU_s || a.sku_s].filter(Boolean)
          })
          .flat()
        if (allVariantSkus.includes(props.sku)) {
          variantFlag = true
        }
        if (
          compareData?.category !== "" &&
          compareData?.category !== productCategory
        ) {
          setErrorMessage(staticTexts?.sameCategory)
          setTimeout(() => {
            setErrorMessage("")
          }, 2000)
          setIsChecked(false)
          onRemove(sku)
        } else if (
          variantFlag &&
          COMPARE_VERSION_CHECK === "compare_products_v2"
        ) {
          setErrorMessage(staticTexts?.sameVariants)
          setTimeout(() => {
            setErrorMessage("")
          }, 2000)
          setIsChecked(false)
          props.onRemove(props.sku)
        } else {
          onAdd(sku)
        }
      } else {
        setErrorMessage(staticTexts?.maxProduct)
        setTimeout(() => {
          setErrorMessage("")
        }, 2000)
        setIsChecked(false)
        onRemove(sku)
      }
    } else {
      onRemove(sku)
    }

    const kohlerDatalayer = window.kohler.datalayer
    kohlerDatalayer.publish(kohlerDatalayer.EventTypes.CLICK, {
      eventMetaData: {
        experience: "pdp",
        eventType: "addToCompare",
        params: {
          isChecked,
          analyticData: productAnalyticsData?.stringProductInfo,
        },
      },
    })
  }

  const handleLogin = e => {
    e.preventDefault()
    e.stopPropagation()
    const productInfoData = JSON.parse(
      productAnalyticsData.stringProductInfo ?? "{}"
    )
    const productInfo = productInfoData?.productInfo
    isSaveProductToList
      ? dataLayerSavetolistGuest(productInfo)
      : addDataLayerFavGuestUser()
    window.loginFavouriteCallback = () => {
      setLoginFavouriteCallback(e)
    }
    dispatch(setAuthModalVisibility({ show: true }))
  }

  const productAnalyticsData = productData || {}

  useEffect(() => {
    if (
      !isEnable &&
      compareWasFocusedRef.current &&
      compareCheckboxRef.current
    ) {
      const node = compareCheckboxRef.current
      compareWasFocusedRef.current = false
      requestAnimationFrame(() => node.focus())
    }
  }, [isEnable])

  useEffect(() => {
    setToasterMessage()
  }, [])

  const actionButtonData = {
    setStockAlert,
    setProductStock,
    addToCartButton,
    productAnalyticsData,
    addProductToCart,
    addToCartTxt,
    isBundleProduct,
  }

  const normalizeInlineRatingButton = () => {
    const inlineRatingNode = inlineRatingRef.current

    if (!inlineRatingNode) return

    inlineRatingNode
      .querySelectorAll('.bv_main_rating_button[aria-haspopup="dialog"]')
      .forEach(button => button.removeAttribute("aria-haspopup"))
  }

  useEffect(() => {
    if (disableRatings || isDiscontinued || !inlineRatingRef.current) {
      return undefined
    }

    normalizeInlineRatingButton()

    const observer = new MutationObserver(() => {
      normalizeInlineRatingButton()
    })

    observer.observe(inlineRatingRef.current, {
      attributes: true,
      attributeFilter: ["aria-haspopup"],
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [disableRatings, isDiscontinued, masterSku, sku])

  const handleInlineRatingClick = e => {
    const interactiveTarget = e.target?.closest(
      'a, button, input, select, textarea, [role="button"], [role="menuitem"], [aria-haspopup], [aria-expanded], [tabindex]'
    )

    // Allow Bazaarvoice controls to handle their own interaction semantics.
    if (interactiveTarget) return

    handleNavChange(4, true)
  }

  return (
    <div className={styles.addToCartWrapper}>
      <div className="row no-gutters buy-area" ref={ref}>
        <div
          className={`add-to-cart col-12 ${
            isDiscontinued ? "add-to-cart__discontinued" : ""
          }`}
          onClick={e =>
            handleProductSuggestion(e, productAnalyticsData?.stringProductInfo)
          }
        >
          {!isDiscontinued &&
          isBackorderable &&
          productStock <= 0 &&
          leadTime ? (
            <div className="product-detail-page__backorder-msg">
              <span>
                <img
                  className="backorder-icon"
                  src={toolTip.src}
                  alt={staticTexts?.toolTip}
                />
              </span>
              <div className="backorder-msg">
                <p>{staticTexts?.backOrderMssg(leadTime)}</p>
              </div>
            </div>
          ) : null}
          {showBuyBoxArea ? (
            isDiscontinued ? (
              isColorDiscontinued ? (
                <div className="product-detail-page__available-color-section">
                  <Button
                    role="button"
                    className="product-detail-page__available-color-btn"
                    onClick={onAvailableColor}
                    flexible={true}
                    label={availColorCTA}
                    size="large"
                    type="primary"
                  />
                </div>
              ) : null
            ) : !(productStock > 0) && !disableEcommerce ? (
              specialOrder === "Yes" ||
              (backOrder === "Yes" && !isBundleProduct) ? (
                <>
                  {stockAlert.show && (
                    <div className="product-detail-page__stock-alert">
                      <img alt="" src={infoIcon} />
                      <span>{stockAlert.mssg}</span>
                    </div>
                  )}
                  {isRegionSoldOnline ? (
                    <AddToCartActions
                      {...props}
                      data={actionButtonData}
                      onQtyChange={qty => onQtyChange(qty)}
                    />
                  ) : null}
                </>
              ) : (isPdpPropsLoaded && isRegionSoldOnline) ||
                isBundleProduct ? (
                <OutOfStockSection
                  outOfStockMsg={outOfStockMsg}
                  localStoreOrShowroomRef={localStoreOrShowroomRef}
                  findStoreUrl={findStoreUrl}
                  sku={sku}
                  staticTexts={staticTexts}
                  productData={productData}
                />
              ) : null
            ) : (
              <>
                {stockAlert.show && (
                  <div className="product-detail-page__stock-alert">
                    <img alt="" src={infoIcon?.src} />
                    <span>{stockAlert.mssg}</span>
                  </div>
                )}
                {isRegionSoldOnline || isBundleProduct ? (
                  <AddToCartActions
                    {...props}
                    data={actionButtonData}
                    onQtyChange={qty => onQtyChange(qty)}
                  />
                ) : null}
              </>
            )
          ) : null}

          {replaceAddToCart &&
          !isDiscontinued &&
          !ProductPriceSpiderIncluded_s ? (
            <div className="replace-add-to-cart-container">
              <Button
                className={`${
                  disableEcommerce ? "replace-add-to-cart-button" : ""
                }`}
                onClick={() => {
                  replaceAddToCartDatalayerPush(
                    replaceAddToCartCtaLabel,
                    replaceAddToCartCtaLink,
                    analyticData
                  )
                  router.push(replaceAddToCartCtaLink)
                }}
                role="button"
                ariaLabel={replaceAddToCartCtaLabel}
                label={replaceAddToCartCtaLabel}
                size={`${disableEcommerce ? " " : "large"}`}
                type="primary"
                flexible={true}
                to={replaceAddToCartCtaLink}
                target={linkNewTab ? "_blank" : "_self"}
              />
            </div>
          ) : null}

          {!isDiscontinued && (
            <div className="bottom-section ">
              {!isBundleProduct ? (
                <div className="add-to-compare">
                  <Checkbox
                    value={compareTxt}
                    checked={isChecked}
                    onChange={handleCompare}
                    disabled={isEnable}
                    inputRef={compareCheckboxRef}
                    checkBoxAria={compareTxt}
                  />
                  {errorMessage !== "" && <Tooltip message={errorMessage} />}
                </div>
              ) : null}

              <div
                className={cx({
                  favorites: true,
                  "favorites--discontinued": enableFav,
                })}
                id="kf-pdp-favorites"
                data-gbh-data-layer={getAnalyticsData(
                  favTxt,
                  "",
                  "",
                  productAnalyticsData?.pdpType === "bundle" ? true : ""
                )}
                data-gbh-data-layer-custom={
                  productAnalyticsData.stringProductInfo
                }
                onClick={
                  isAuth
                    ? e =>
                        handleFavorite(
                          e,
                          getFavIconStatus({
                            isSaveProductToList,
                            productSavedToList,
                            inSavedItems: userListItemsSkus[sku],
                            isFavorite: showFavirote,
                            filledWishIcon,
                            wishIcon,
                          })
                        )
                    : handleLogin
                }
                role="button"
                tabIndex="0"
                data-gbh-clickable="true"
              >
                <img
                  src={getFavIconStatus({
                    isSaveProductToList,
                    productSavedToList,
                    inSavedItems: userListItemsSkus[sku],
                    isFavorite: showFavirote,
                    filledWishIcon,
                    wishIcon,
                  })}
                  alt=""
                />
                <span className="text">{listActionText}</span>
              </div>
              {!isDiscontinued && !disableRatings ? (
                <div
                  ref={inlineRatingRef}
                  data-bv-show="rating_summary"
                  data-bv-product-id={masterSku || sku}
                  data-bv-seo="false"
                  className="product-detail-page__inline-rating"
                  onClick={handleInlineRatingClick}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
      {openModal ? (
        <ListConfirmationModal
          openConforMationModal={openModal}
          deleteFromList={deleteFromList}
          closeConformationModal={() => setOpenModal(false)}
          list={userListItemsSkus[sku]}
          deleteConfirmationModal={() => setProductSavedToList(false)}
          handleSuccessCallBack={handleDeleteFromListSuccessCallback}
        />
      ) : showAddToList ? (
        <SaveToList
          productInfoPdp={productInfo}
          productSku={sku}
          showListSaveAndCreate={showAddToList}
          listSaveAndCreateClose={handleListSaveAndCreateClose}
          handleSuccessCallBack={handleSaveToListSuccessCallBack}
        />
      ) : null}
    </div>
  )
})

AddToCart.propTypes = {
  staticTexts: PropTypes.object,
  masterSku: PropTypes.string,
}

AddToCart.displayName = "AddToCart"

export default AddToCart
